// FavoritesHistoryPage.jsx 
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./baseStyles.css";
import "./FavoritesHistoryPage.css"; // ← new, mobile-first styles

const apiUrl = process.env.REACT_APP_API_URL;
const HISTORY_OPERATION = "history_favorites";
const TZ = "America/Chicago";

const FavoritesHistoryPage = () => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [loginUserId, setLoginUserId] = useState("");

  const [favCampaigns, setFavCampaigns] = useState([]);
  const [campaignName, setCampaignName] = useState("");

  const [rows, setRows] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);

  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const idToken = useMemo(() => localStorage.getItem("idToken"), []);

  // ---------- utils ----------
  const clearStatus = () => {
    setError("");
    setInfoMessage("");
  };

  const authGuard = useCallback(() => {
    if (!idToken) {
      setError("User is not authenticated. Please log in.");
      navigate("/login");
      return null;
    }
    try {
      const decoded = jwtDecode(idToken);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return null;
      }
      return decoded;
    } catch (e) {
      console.error("❌ Token decode failed", e);
      localStorage.removeItem("idToken");
      setError("Authentication error. Please log in again.");
      navigate("/login");
      return null;
    }
  }, [idToken, navigate]);

  const displayCampaignName = (raw) => {
    if (!raw) return "";
    let d = raw;
    if (userId && d.startsWith(`${userId}_`)) d = d.replace(`${userId}_`, "");
    return d;
  };

  const shortDate = (row) => {
    const fmt = (d) =>
      d.toLocaleDateString(undefined, {
        timeZone: TZ,
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      });
    if (row.lastAccessedReadable) {
      const isoish = row.lastAccessedReadable.replace(" ", "T") + "Z";
      const d = new Date(isoish);
      if (!isNaN(d)) return fmt(d);
    }
    if (row.lastAccessed != null && row.lastAccessed !== "") {
      const n = Number(row.lastAccessed);
      if (!Number.isNaN(n)) {
        const ms = n > 1e12 ? n : n * 1000;
        const d = new Date(ms);
        if (!isNaN(d)) return fmt(d);
      }
    }
    return "";
  };

  const possessive = (name) => {
    if (!name) return "";
    const n = String(name).trim();
    return /s$/i.test(n) ? `${n}’` : `${n}’s`;
  };

  const Badge = ({ ok }) =>
    ok == null ? (
      <span className="badge dash">—</span>
    ) : ok ? (
      <span className="badge good" role="img" aria-label="correct">
        ✅
      </span>
    ) : (
      <span className="badge bad" role="img" aria-label="incorrect">
        ❌
      </span>
    );

  // ---------- lifecycle ----------
  useEffect(() => {
    clearStatus();
    const decoded = authGuard();
    if (!decoded) return;

    setUserId(decoded.sub);
    setLoginUserId(decoded["cognito:username"]);
    void fetchCampaigns(decoded.sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampaigns = async (uid) => {
    setLoadingCampaigns(true);
    try {
      const res = await axios.post(
        `${apiUrl}`,
        { operation: "fetch_all", userid: uid },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const data = Array.isArray(res.data) ? res.data : [];

      const filtered = data.filter(
        (c) =>
          c.campaignListCampaignType === "Favorites" ||
          /favorites/i.test(c.campaignName || "")
      );

      setFavCampaigns(filtered);

      if (filtered.length === 1) {
        const only = filtered[0].campaignName;
        setCampaignName(only);
        await fetchHistory(uid, only);
      }
    } catch (e) {
      console.error("❌ fetch_all failed", e);
      if (e?.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      setError("Failed to load campaigns.");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const fetchHistory = async (uid, cName) => {
    if (!uid || !cName) return;
    clearStatus();
    setLoadingRows(true);
    setRows([]);

    try {
      const res = await axios.post(
        `${apiUrl}`,
        {
          operation: HISTORY_OPERATION,
          userid: uid,
          campaignName: cName,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const items = Array.isArray(res.data) ? res.data : [];

      // ---------- Normalize (NO recompute; use DB values) ----------
      const normalized = items.map((it) => {
        const partners = Array.isArray(it.partnerResponses)
          ? it.partnerResponses.slice()
          : [];

        const viewerAnswers = {
          Answer1:
            it.Answer1 ??
            it?.AnswersAndGuesses?.Answer1 ??
            it?.viewerAnswers?.Answer1 ??
            "",
          Answer2:
            it.Answer2 ??
            it?.AnswersAndGuesses?.Answer2 ??
            it?.viewerAnswers?.Answer2 ??
            "",
        };

        const viewerGuessesTop = {
          Guess1:
            it.Guess1 ??
            it?.AnswersAndGuesses?.Guess1 ??
            it?.viewerGuesses?.Guess1 ??
            "",
          Guess2:
            it.Guess2 ??
            it?.AnswersAndGuesses?.Guess2 ??
            it?.viewerGuesses?.Guess2 ??
            "",
        };

        return {
          sequenceNumber:
            it.sequenceNumber ?? it.seq ?? Number(it.campaignItemsId) ?? 0,
          campaignItemsId: it.campaignItemsId ?? it.itemId ?? "",
          campaignItemsText:
            it.campaignItemsText ??
            it.questionText ??
            it.category ??
            "Favorite",
          lastAccessed: it.lastAccessed ?? it.dataAccessed ?? null,
          lastAccessedReadable:
            it.lastAccessedReadable ?? it.dataAccessedReadable ?? "",

          viewerAnswers,
          viewerGuesses: viewerGuessesTop,

          partners: partners
            .filter(
              (p) =>
                (p.partnerUserId && p.partnerUserId !== uid) ||
                (p.partnerLoginId && p.partnerLoginId !== loginUserId)
            )
            .map((p) => {
              const favorite1 = p.partnerAnswer1 ?? p.Answer1 ?? "";
              const favorite2 = p.partnerAnswer2 ?? p.Answer2 ?? "";
              const theirGuess1 = p.partnerGuess1 ?? p.Guess1 ?? "";
              const theirGuess2 = p.partnerGuess2 ?? p.Guess2 ?? "";
              const yourGuess1 = p.viewerGuess1 ?? viewerGuessesTop.Guess1 ?? "";
              const yourGuess2 = p.viewerGuess2 ?? viewerGuessesTop.Guess2 ?? "";

              // Stored match flags from DB (do NOT compute here)
              const yourMatch1 =
                typeof p.yourMatch1 === "boolean" ? p.yourMatch1 : null;
              const yourMatch2 =
                typeof p.yourMatch2 === "boolean" ? p.yourMatch2 : null;
              const theirMatch1 =
                typeof p.theirMatch1 === "boolean" ? p.theirMatch1 : null;
              const theirMatch2 =
                typeof p.theirMatch2 === "boolean" ? p.theirMatch2 : null;

              return {
                partnerUserId: p.partnerUserId ?? "",
                partnerLoginId: p.partnerLoginId ?? "",
                partnerFirstName:
                  p.partnerFirstName ?? p.partnerLoginId ?? "Partner",
                favorite1,
                favorite2,
                yourGuess1,
                yourGuess2,
                theirGuess1,
                theirGuess2,
                yourMatch1,
                yourMatch2,
                theirMatch1,
                theirMatch2,
              };
            }),
        };
      });

      // Hide "current" item: drop highest sequenceNumber
      const withSeq = normalized.filter((r) => r.sequenceNumber > 0);
      const maxSeq = withSeq.reduce(
        (m, r) => Math.max(m, r.sequenceNumber || 0),
        0
      );
      const historyOnly = withSeq.filter((r) => r.sequenceNumber !== maxSeq);

      // Sort newest first
      setRows(
        historyOnly.sort(
          (a, b) => (b.sequenceNumber ?? 0) - (a.sequenceNumber ?? 0)
        )
      );
    } catch (e) {
      console.error("❌ favorites history fetch failed", e);
      if (e?.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      setError("Failed to load favorites history.");
    } finally {
      setLoadingRows(false);
    }
  };

  const onChangeCampaign = async (val) => {
    setCampaignName(val);
    await fetchHistory(userId, val);
  };

  // ---------- NEW: mark partner's match as true ----------
  const handleMarkPartnerMatch = async (row, partner, matchIndex) => {
    if (!partner.partnerUserId) {
      setError("Missing partner id for this row.");
      return;
    }

    clearStatus();

    try {
      await axios.post(
        `${apiUrl}`,
        {
          operation: "update_favorites_partner_match",
          userid: userId,
          campaignName,
          campaignItemsId: row.campaignItemsId || row.sequenceNumber,
          partnerUserId: partner.partnerUserId,
          matchIndex,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      // Update local state so UI reflects the change
      setRows((prev) =>
        prev.map((r) => {
          const sameRow =
            (r.campaignItemsId || r.sequenceNumber) ===
            (row.campaignItemsId || row.sequenceNumber);
          if (!sameRow) return r;
          return {
            ...r,
            partners: r.partners.map((p) => {
              if (p.partnerUserId !== partner.partnerUserId) return p;
              return {
                ...p,
                ...(matchIndex === 1
                  ? { theirMatch1: true }
                  : { theirMatch2: true }),
              };
            }),
          };
        })
      );

      setInfoMessage("Updated partner match.");
    } catch (e) {
      console.error("❌ update_favorites_partner_match failed", e);
      if (e?.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      setError("Failed to update partner match.");
    }
  };

  // ---------- UI ----------
  return (
    <div className="container fav-history">
      <div className="header-with-info">
        <h5 className="section-header">Favorites — History</h5>
      </div>

      {error && <p className="error-message">{error}</p>}
      {infoMessage && <p className="success-message">{infoMessage}</p>}

      {/* Campaign selector */}
      {loadingCampaigns ? (
        <div>Loading campaigns…</div>
      ) : favCampaigns.length ? (
        <div className="form-group dropdown-container" style={{ marginTop: 8 }}>
          <select
            className="compact-select"
            value={campaignName}
            onChange={(e) => onChangeCampaign(e.target.value)}
          >
            <option value="">-- Select Campaign --</option>
            {favCampaigns.map((c) => (
              <option key={c.campaignName} value={c.campaignName}>
                {displayCampaignName(c.campaignName)}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p>No Favorites campaigns found.</p>
      )}

      {/* Cards */}
      <div className="cards">
        {loadingRows ? (
          <div>Loading history…</div>
        ) : !campaignName ? (
          <p>Select a campaign to view history.</p>
        ) : rows.length === 0 ? (
          <p>No past favorites yet.</p>
        ) : (
          rows.map((r) => (
            <div
              className="card fav-card"
              key={r.campaignItemsId || `${r.sequenceNumber}`}
            >
              {/* Header: seq + date + question */}
              <div className="fav-card-head">
                <h4 className="fav-seq">
                  #{r.sequenceNumber}
                  {shortDate(r) ? (
                    <span className="fav-date"> • {shortDate(r)}</span>
                  ) : null}
                </h4>
              </div>
              <div className="fav-question">{r.campaignItemsText}</div>

              {/* Partners */}
              <div className="partner-list">
                {r.partners.length === 0 ? (
                  <div className="response-card">
                    <em className="muted">No partner responses.</em>
                  </div>
                ) : (
                  r.partners.map((p, idx) => (
                    <div
                      key={`${r.sequenceNumber}-p-${idx}`}
                      className="response-card"
                    >
                      {/* Partner favorites */}
                      <div className="section-title">
                        {possessive(
                          p.partnerFirstName || p.partnerLoginId || "Partner"
                        )}{" "}
                        favorites
                      </div>
                      <div className="kv">
                        <div className="kv-row">
                          <span className="k">Most Favorite:</span>
                          <span className="v">
                            {p.favorite1 || <em className="muted">—</em>}
                          </span>
                        </div>
                        <div className="kv-row">
                          <span className="k">2nd Favorite:</span>
                          <span className="v">
                            {p.favorite2 || <em className="muted">—</em>}
                          </span>
                        </div>
                      </div>

                      <hr className="divider" />

                      {/* Your guesses about them (show stored match flags from DB only) */}
                      {(p.yourGuess1 || p.yourGuess2) && (
                        <>
                          <div className="section-subtitle">You guessed:</div>
                          <div className="kv">
                            <div className="kv-row">
                              <span className="k">
                                <Badge ok={p.yourMatch1} /> for most favorite:
                              </span>
                              <span className="v">
                                {p.yourGuess1 || (
                                  <em className="muted">—</em>
                                )}
                              </span>
                            </div>
                            <div className="kv-row">
                              <span className="k">
                                <Badge ok={p.yourMatch2} /> for 2nd favorite:
                              </span>
                              <span className="v">
                                {p.yourGuess2 || (
                                  <em className="muted">—</em>
                                )}
                              </span>
                            </div>
                          </div>
                          <hr className="divider1" />
                        </>
                      )}

                      {/* Your favorites (context) */}
                      {(r.viewerAnswers.Answer1 || r.viewerAnswers.Answer2) && (
                        <>
                          <div className="section-title">Your favorites</div>
                          <div className="kv">
                            <div className="kv-row">
                              <span className="k">Most Favorite:</span>
                              <span className="v">
                                {r.viewerAnswers.Answer1 || (
                                  <em className="muted">—</em>
                                )}
                              </span>
                            </div>
                            <div className="kv-row">
                              <span className="k">2nd Favorite:</span>
                              <span className="v">
                                {r.viewerAnswers.Answer2 || (
                                  <em className="muted">—</em>
                                )}
                              </span>
                            </div>
                          </div>
                          <hr className="divider" />
                        </>
                      )}

                      {/* Their guesses about you (show stored match flags from DB only) */}
                      {(p.theirGuess1 || p.theirGuess2) && (
                        <>
                          <div className="section-subtitle">
                            {possessive(
                              p.partnerFirstName || p.partnerLoginId || "Partner"
                            )}{" "}
                            guessed
                          </div>

                          <div className="kv">
                            <div className="kv-row">
                              <span className="k">
                                <Badge ok={p.theirMatch1} /> for most:
                              </span>
                              <span className="v">
                                {p.theirGuess1 || (
                                  <em className="muted">—</em>
                                )}

                                {/* Simple "mark as match" on mismatches */}
                                {p.theirGuess1 &&
                                  p.theirMatch1 === false && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleMarkPartnerMatch(r, p, 1)
                                      }
style={{
  marginLeft: "8px",
  fontSize: "0.75rem",
  border: "1px solid #d1d5db",
  background: "#f9fafb",
  padding: "2px 6px",
  borderRadius: "6px",
  cursor: "pointer",
  color: "#374151",
}}

                                    >
                                      set matched
                                    </button>
                                  )}
                              </span>
                            </div>
                            <div className="kv-row">
                              <span className="k">
                                <Badge ok={p.theirMatch2} /> for 2nd most:
                              </span>
                              <span className="v">
                                {p.theirGuess2 || (
                                  <em className="muted">—</em>
                                )}

                                {p.theirGuess2 &&
                                  p.theirMatch2 === false && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleMarkPartnerMatch(r, p, 2)
                                      }
style={{
  marginLeft: "8px",
  fontSize: "0.75rem",
  border: "1px solid #d1d5db",
  background: "#f9fafb",
  padding: "2px 6px",
  borderRadius: "6px",
  cursor: "pointer",
  color: "#374151",
}}

                                    >
                                      set matched
                                    </button>
                                  )}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </div>

<br/>
      <button
        className="save-button back-btn"
        onClick={() => navigate("/myfavorites")}
      >
        Back to My Favorites
      </button>
<br/>
      <button
        className="save-button back-btn"
        onClick={() => navigate("/list-manager")}
      >
        Back to Campaigns
      </button>
    </div>
  );
};

export default FavoritesHistoryPage;
